import {Component, DestroyRef, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {ToastrService} from 'ngx-toastr';
import {ModalComponent} from '../../../../shared/components/modal/modal.component';
import {UserGroup} from '../../../../classes/models/user/UserGroup';
import {UserGroupsService} from '../../../../services/http/user/user-groups.service';
import {SHARED_IMPORTS} from "../../../../shared/standalone-imports";

/**
 * Defines the data required by the user group deletion modal.
 */
export interface UserGroupsDeleteModalData {
  userGroup: UserGroup;
}

/**
 * This component handles deleting a user group.
 */
@Component({
  selector: 'app-user-groups-delete',
  templateUrl: './user-groups-delete.component.html',
  styleUrls: ['./user-groups-delete.component.less'],
  imports: [SHARED_IMPORTS]
})
export class UserGroupsDeleteComponent {
  /** A user group to be deleted. */
  readonly userGroup = signal<UserGroup>(undefined!);
  /** `True` if request to backend is in progress. */
  readonly loading = signal(false);
  private readonly userGroupsService = inject(UserGroupsService);
  private readonly toastr = inject(ToastrService);
  private readonly destroyRef = inject(DestroyRef);

  static open(modalService: NgbModal, data: UserGroupsDeleteModalData): NgbModalRef {
    const modalRef = modalService.open(UserGroupsDeleteComponent);
    const instance = modalRef.componentInstance as UserGroupsDeleteComponent;
    instance.userGroup.set(data.userGroup);
    return modalRef;
  }

  /**
   * Sends a request to backend to delete a user group.
   * @param modalComponent a modal component of a user group deletion form.
   */
  confirm(modalComponent: ModalComponent): void {
    this.loading.set(true);

    this.userGroupsService.deleteUserGroup(this.userGroup().email)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                modalComponent.activeModal.close();
                this.loading.set(false);
                this.toastr.success('User group has been successfully deleted');
              },
              error: (error) => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }
}